import { useState } from 'react'
import { Link } from 'react-router'
import { Button, Hinweisdialog } from './primitives'
import { baseUrl } from '@/services/catalog'
import { downloadAll, type DownloadProgress } from '@/features/offline/storage'
import { beschreibeUpdates } from '@/features/updates/diff'
import { useApp } from '@/store/app'

/**
 * Hinweis auf geänderte Taschenkarten.
 *
 * Die verlässliche Hälfte des Melde-Weges: Push erreicht nur, wer ihm zugestimmt hat
 * und ein Gerät benutzt, das ihn unterstützt — dieser Hinweis erscheint jedem beim
 * Öffnen der App.
 *
 * „Später" markiert bewusst nichts als gelesen. Bei Einsatzunterlagen soll ein
 * unbeachteter Änderungshinweis beim nächsten Start wiederkommen. Er verschwindet,
 * sobald die Karten geladen oder gelesen wurden.
 *
 * Ob dieser Dialog an der Reihe ist, entscheidet `Hinweise.tsx`.
 */
export function ContentUpdatePrompt({ onSpäter }: { onSpäter: () => void }) {
  const updates = useApp((s) => s.updates)
  const refreshOfflineStates = useApp((s) => s.refreshOfflineStates)
  const markUpdatesSeen = useApp((s) => s.markUpdatesSeen)

  const [progress, setProgress] = useState<DownloadProgress | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const betroffen = [...updates.neu, ...updates.aktualisiert]

  const laden = async () => {
    setFehler(null)
    setProgress({ done: 0, total: betroffen.length, current: '' })

    // Nur die gemeldeten Karten, nicht der gesamte Bestand: Der Hinweis spricht von
    // drei Karten, also dürfen auch nur drei geladen werden.
    const ergebnis = await downloadAll(betroffen, baseUrl, setProgress)

    setProgress(null)
    await refreshOfflineStates()

    if (ergebnis.failed.length > 0) {
      setFehler(
        `${ergebnis.failed.length} von ${betroffen.length} Karten konnten nicht geladen werden. ` +
          'Unter „Mehr" lässt sich der Versuch wiederholen.',
      )
      return
    }
    await markUpdatesSeen()
  }

  return (
    <Hinweisdialog
      titel="Geänderte Taschenkarten"
      // Während des Ladens bleibt der Dialog stehen: Ein halb weggeklickter Vorgang
      // hinterlässt einen Teil der Karten geladen und keinen Hinweis darauf.
      onAbbruch={progress ? () => {} : onSpäter}
    >
      <p className="text-sm text-ink">
        <strong className="font-semibold">{beschreibeUpdates(updates)}</strong>{' '}
        {betroffen.length <= 3 ? (
          <>
            Betroffen {betroffen.length === 1 ? 'ist' : 'sind'}{' '}
            {betroffen.map((card, i) => (
              <span key={card.id}>
                {i > 0 && (i === betroffen.length - 1 ? ' und ' : ', ')}
                {/* Titel in Anführungszeichen: „Warntafel und Gefahrzettel" enthält
                    selbst ein „und" — ohne Abgrenzung liest sich die Aufzählung als
                    eine Karte mehr, als es sind. */}
                „
                <Link to={`/karte/${card.id}`} className="underline underline-offset-2">
                  {card.title}
                </Link>
                “
              </span>
            ))}
            .
          </>
        ) : (
          <>
            Im{' '}
            <Link to="/katalog" className="underline underline-offset-2">
              Katalog
            </Link>{' '}
            sind sie gekennzeichnet.
          </>
        )}
      </p>

      {progress ? (
        <div className="mt-3">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate text-ink">{progress.current || 'Wird abgeschlossen …'}</span>
            <span className="shrink-0 tabular-nums text-muted">
              {progress.done} / {progress.total}
            </span>
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.done}
            className="mt-2 h-1.5 w-full overflow-hidden rounded-chip bg-sunken"
          >
            <div
              className="h-full bg-accent transition-[width] duration-200"
              style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-3">
          <Button variant="primary" onClick={() => void laden()}>
            Jetzt laden
          </Button>
          <button
            type="button"
            onClick={onSpäter}
            className="tap -mr-2 shrink-0 px-2 text-sm text-muted hover:text-ink"
          >
            Später
          </button>
        </div>
      )}

      {fehler && <p className="mt-2 text-sm text-muted">{fehler}</p>}
    </Hinweisdialog>
  )
}
